import Event from "sap/ui/base/Event";
import JSONModel from "sap/ui/model/json/JSONModel";
import BaseController from "./BaseController";
import AuthService from "../service/AuthService";
import OidcService, { OidcError } from "../service/OidcService";

/**
 * Another application's sign-in, continued in the portal (#/oidc/<uid>).
 *
 * Signed in to this tab: finish it and send the browser back. Not signed in,
 * or the application wants a sign-in now (prompt=login): remember it and show
 * the login page; every sign-in path comes back here afterwards.
 *
 * @namespace com.ang.portal.controller
 */
export default class OidcContinue extends BaseController {
	public onInit(): void {
		this.setModel(new JSONModel({ message: "", error: "" }), "oidc");
		this.getRouter()
			.getRoute("oidcContinue")
			.attachPatternMatched(
				(oEvent: Event<{ arguments?: { uid?: string } }>) =>
					void this.continueSignIn(oEvent.getParameter("arguments")?.uid),
				this,
			);
	}

	private async continueSignIn(uid: string | undefined): Promise<void> {
		const oModel = this.getModel("oidc") as JSONModel;
		const bundle = await this.getResourceBundle();
		oModel.setData({ message: bundle.getText("oidcContinueWorking"), error: "" });

		if (!uid) {
			oModel.setProperty("/error", bundle.getText("oidcContinueExpired"));
			return;
		}

		let appName = "";
		try {
			const interaction = await OidcService.getInteraction(uid);
			appName = interaction.client.name;
			oModel.setProperty("/message", bundle.getText("oidcContinueSigningIn", [appName]));

			// A sign-in the application asked to happen now is not satisfied by
			// the one this tab already holds - unless the user just made it.
			const justSignedIn = OidcService.consumeContinued(uid);
			if (interaction.freshSignIn && !justSignedIn) {
				AuthService.clearLocalSession();
			}

			if (!AuthService.isAuthenticated()) {
				this.signInFirst(uid, appName);
				return;
			}

			window.location.replace(await OidcService.confirm(uid));
		} catch (error) {
			if (error instanceof OidcError && error.status === 401) {
				// The tab's session has ended; sign in again and come back.
				AuthService.clearLocalSession();
				this.signInFirst(uid, appName);
				return;
			}
			oModel.setProperty(
				"/error",
				error instanceof OidcError && error.status === 410
					? bundle.getText("oidcContinueExpired")
					: error instanceof Error && error.message
						? error.message
						: bundle.getText("oidcContinueFailed"),
			);
		}
	}

	private signInFirst(uid: string, appName: string): void {
		OidcService.rememberPending({ uid, appName });
		this.navTo("login", {}, true);
	}

	public onGoToPortal(): void {
		this.navTo(AuthService.isAuthenticated() ? "home" : "login", {}, true);
	}
}
